import { memo, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useReleasesTableController } from '@/features/releases/components/useReleasesTableController';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import type { ReleasesTableProps } from '@/features/releases/components/ReleasesTable.types';
import type { ReleaseListItemDto } from '@flowqa/shared';

const ReleasesTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  initialData,
  disablePageSizeSelect,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'release' }),
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: ReleasesTableProps): ReactElement => {
  const {
    columns,
    deleteLoading,
    deleteTarget,
    gridConfig,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    selectedOrganisationId,
    selectedProjectId,
  } = useReleasesTableController({
    initialSortModel,
    initialFilterModel,
    persistKey,
    queryFn,
    queryKey,
    syncStateToUrl,
  });

  return (
    <>
      <ServerDataTable<ReleaseListItemDto>
        queryPath="releases/query"
        {...gridConfig}
        fixedFilters={fixedFilters ?? gridConfig.fixedFilters}
        initialData={initialData}
        queryFn={resolvedQueryFn}
        queryKey={resolvedQueryKey}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        disablePageSizeSelect={disablePageSizeSelect}
        noRowsText={noRowsText}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        onRowsChange={onRowsChange}
        onQueryMeta={onQueryMeta}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        showLastUpdated={showLastUpdated}
        columnVisibilityModel={columnVisibilityModel}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
        }}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="name"], & .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="features"], & .MuiDataGrid-cell[data-field="latestSuiteResults"], & .MuiDataGrid-cell[data-field="latestTestResults"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
          },
        ]}
        autoHeight
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'release' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'release',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'release',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};

export const ReleasesTable = memo(ReleasesTableComponent);
